import { api } from './api-client';

/**
 * The people on the platform, as `/v1/admin/users` serves them.
 *
 * Declared here rather than imported from the API: ADR 0001 settles that types
 * are duplicated between the API and its clients rather than shared through a
 * workspace. The shape has to keep matching `api/src/users/admin-users.service.ts`.
 *
 * Note what is not in these types. There is no password hash, no `appleSub` and
 * no `googleSub` — the API answers which ways in an account has as three
 * booleans and never sends the values, because an operator has no use for a
 * third party's identifier for a person and a screen showing one can be
 * screenshotted.
 */

/** Which side of the anonymous/account line to list. */
export type UserFilter = 'registered' | 'anonymous' | 'all';

export interface UserSummary {
  id: string;
  /** Null for an anonymous row, and for an Apple account that shared no address. */
  email: string | null;
  /** Apple returns this on the first authorization only, so it is often null. */
  displayName: string | null;
  /** Null means anonymous — the ordinary state, not a pending one. */
  registeredAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  hasApple: boolean;
  hasGoogle: boolean;
  hasPassword: boolean;
}

export interface UserPage {
  users: UserSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UserEvent {
  id: string;
  /** A primitive fact name, e.g. `app.first_run`. Never a milestone. */
  type: string;
  /** The server clock, which is what the funnel is computed on. */
  recordedAt: string;
  /** What the device claimed, and untrusted. Usually null. */
  occurredAt: string | null;
  context: Record<string, unknown> | null;
}

/** A conversation's metadata. The transcript is deliberately not served. */
export interface UserConversation {
  id: string;
  languageCode: string;
  status: string;
  endedReason: string | null;
  turns: number;
  estimateBand: string | null;
  estimatedAt: string | null;
  createdAt: string;
}

export interface UserSession {
  id: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  /** Client-supplied, recorded for audit. Never a fact about where somebody was. */
  userAgent: string | null;
  ipAddress: string | null;
}

export interface UserDetail extends UserSummary {
  emailVerified: boolean;
  failedLoginCount: number;
  lockedUntil: string | null;
  updatedAt: string;
  events: UserEvent[];
  conversations: UserConversation[];
  sessions: UserSession[];
}

export interface ListUsersQuery {
  status?: UserFilter;
  q?: string;
  page?: number;
  pageSize?: number;
}

/**
 * One page of users.
 *
 * The query string is built here rather than in `api-client.ts`. Nothing else
 * in the portal passes parameters, so a helper on the client would be a
 * signature change made for one caller — and `forbidNonWhitelisted` on the API
 * means a stray parameter is a 400 rather than something quietly ignored, so
 * only keys with a value are sent.
 */
export async function listUsers(query: ListUsersQuery = {}): Promise<UserPage> {
  const params = new URLSearchParams();

  if (query.status) params.set('status', query.status);
  if (query.q?.trim()) params.set('q', query.q.trim());
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));

  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return api.get<UserPage>(`/admin/users${suffix}`);
}

export async function getUser(id: string): Promise<UserDetail> {
  const { user } = await api.get<{ user: UserDetail }>(`/admin/users/${id}`);
  return user;
}

/** Which ways in an account has, for a row of badges. */
export function providersOf(user: UserSummary): string[] {
  const providers: string[] = [];

  if (user.hasApple) providers.push('Apple');
  if (user.hasGoogle) providers.push('Google');
  if (user.hasPassword) providers.push('Email');

  return providers;
}
